// BlogIndex.tsx — Blog homepage
// Default view: search → tabs → featured hero + editor's picks → trending → browse by topic → paginated grid
// Filtered/search view: search → tabs → results grid (editorial sections hidden)
// All section placement is automatic/deterministic — see deriveSections(). No manual curation.
// Style: dark #0a0c0c bg, #111 cards, #57d7e2 cyan accent, Barlow font

import { useState, useMemo, useEffect, useRef, Fragment } from "react";
import { Link } from "wouter";
import SiteLayout from "@/components/SiteLayout";
import NewsletterForm from "@/components/NewsletterForm";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import LLMVisibilityPromoBlock from "@/components/LLMVisibilityPromoBlock";
import CoverImage from "@/components/CoverImage";
import { hasDiagram, DIAGRAM_SLUGS } from "@/components/blog-diagrams/index";
import { flagshipPosts } from "@/lib/blogFlagships";
import { matchReason, searchPosts } from "@/lib/blogSearch";
import { longReadLabel, postExcerpt, relativeDate, stripTitleSuffix } from "@/lib/blogText";
import {
  POSTS,
  CATEGORY_LIST,
  getCategoryBySlug,
  getAuthorBySlug,
} from "@/lib/blogMeta.generated";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const PAGE_SIZE = 12;

type Post = (typeof POSTS)[0];

// ── Deterministic section logic ───────────────────────────────────────────────
// Everything below is computed from publishedAt / readingTime / categories.
// Manual `featured` flags are honored as an override but nothing needs to be set.
function byDate(a: Post, b: Post) {
  const d = new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
  if (d !== 0) return d;
  if (b.readingTime !== a.readingTime) return b.readingTime - a.readingTime; // longer first
  return a.slug.localeCompare(b.slug); // stable, never flickers
}

function deriveSections(posts: Post[]) {
  const sorted = [...posts].sort(byDate);

  // The magazine layer. When an editor has curated flagships, they take the
  // hero and the slots beside it; otherwise fall back to the automatic
  // selection so nothing breaks if nobody curates. See lib/blogFlagships.ts.
  const curated = flagshipPosts(sorted);
  const hero = curated[0] ?? (sorted.filter((p) => p.featured)[0] ?? sorted[0]);

  let picks = curated.slice(1, 5);
  /** True when `picks` is a real editorial selection rather than a sort order. */
  const picksAreCurated = picks.length > 0;

  if (!picksAreCurated) {
    // Fallback: the most substantial posts, forced to distinct categories.
    // This is a sort order, not curation — the heading says so.
    const used = new Set<string>();
    const byDepth = [...posts]
      .filter((p) => p.slug !== hero?.slug)
      .sort((a, b) => {
        if (b.readingTime !== a.readingTime) return b.readingTime - a.readingTime;
        return byDate(a, b);
      });
    for (const p of byDepth) {
      const cat = p.categories[0] ?? "_";
      if (used.has(cat)) continue;
      used.add(cat);
      picks.push(p);
      if (picks.length === 4) break;
    }
  }

  // Trending: recency × depth score, excluding hero + picks.
  const excluded = new Set([hero?.slug, ...picks.map((p) => p.slug)]);
  const now = Date.now();
  const maxRead = Math.max(...posts.map((p) => p.readingTime), 1);
  const trending = [...posts]
    .filter((p) => !excluded.has(p.slug))
    .map((p) => {
      const ageDays = (now - new Date(p.publishedAt).getTime()) / 86_400_000;
      const recency = Math.max(0, 1 - ageDays / 180); // fades over ~6 months
      const depth = p.readingTime / maxRead;
      return { p, score: recency * 0.6 + depth * 0.4 };
    })
    .sort((a, b) => b.score - a.score || byDate(a.p, b.p))
    .slice(0, 6)
    .map((x) => x.p);

  return { sorted, hero, picks, picksAreCurated, trending };
}

// ── Category pill ─────────────────────────────────────────────────────────────
function CategoryPill({ slug }: { slug: string }) {
  const cat = getCategoryBySlug(slug);
  if (!cat) return null;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: "4px",
        fontSize: "11px",
        fontWeight: 700,
        fontFamily: ff,
        letterSpacing: "0.07em",
        textTransform: "uppercase" as const,
        color: cat.color,
        background: `${cat.color}18`,
        border: `1px solid ${cat.color}30`,
        whiteSpace: "nowrap" as const,
      }}
    >
      {cat.label}
    </span>
  );
}

// ── Featured post — the one article the homepage argues for ───────────────────
function FeaturedPost({ post }: { post: Post }) {
  const isMobile = useIsMobile();
  const [hov, setHov] = useState(false);
  const author = getAuthorBySlug(post.author);
  const cat = getCategoryBySlug(post.categories[0]);
  const accent = cat?.color ?? "#57d7e2";
  const summary = postExcerpt(post);
  return (
    <Link
      href={`/blog/${post.slug}`}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex",
        flexDirection: "column",
        background: "#111",
        border: `1px solid ${hov ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)"}`,
        borderRadius: "16px",
        overflow: "hidden",
        textDecoration: "none",
        transition: "border-color 0.2s, transform 0.2s",
        transform: hov ? "translateY(-2px)" : "translateY(0)",
        cursor: "pointer",
        minHeight: isMobile ? undefined : "360px",
      }}
    >
      <div style={{ height: isMobile ? "180px" : "240px", overflow: "hidden", flexShrink: 0, position: "relative" }}>
        <CoverImage post={post} eager imgStyle={{
          width: "100%", height: "100%", objectFit: "cover", display: "block",
          transform: hov ? "scale(1.02)" : "scale(1)", transition: "transform 0.3s",
        }} />
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to bottom, transparent 55%, #111)` }} />
      </div>
      <div style={{ height: "3px", background: accent, flexShrink: 0 }} />

      <div style={{
        padding: isMobile ? "26px 22px 28px" : "40px 44px 42px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: isMobile ? "22px" : "30px",
        flex: 1,
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
            <CategoryPill slug={post.categories[0]} />
            <span style={{
              padding: "3px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: 700,
              fontFamily: ff, letterSpacing: "0.07em", textTransform: "uppercase" as const,
              color: "#57d7e2", background: "rgba(87,215,226,0.08)",
              border: "1px solid rgba(87,215,226,0.15)",
            }}>Featured</span>
            {hasDiagram(post.slug) && (
              <span style={{
                padding: "3px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: 700,
                fontFamily: ff, letterSpacing: "0.07em", textTransform: "uppercase" as const,
                color: "#57d7e2", background: "rgba(87,215,226,0.08)",
                border: "1px solid rgba(87,215,226,0.15)",
              }}>Interactive</span>
            )}
          </div>
          <h2 style={{
            color: "#fff",
            fontSize: isMobile ? "28px" : "clamp(30px, 3vw, 42px)",
            fontWeight: 800,
            fontFamily: ff,
            margin: "0 0 18px",
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
            textWrap: "balance" as const,
            maxWidth: "22ch",
          }}>{stripTitleSuffix(post.title)}</h2>
          {summary && (
            <p style={{
              color: "rgba(255,255,255,0.66)",
              fontSize: isMobile ? "16px" : "18px",
              fontFamily: ff,
              lineHeight: 1.6,
              margin: 0,
              maxWidth: "62ch",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical" as const,
              overflow: "hidden",
            }}>{summary}</p>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px", flexWrap: "wrap" }}>
          {author && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img loading="lazy" src={author.avatar} alt=""
                style={{ width: "30px", height: "30px", borderRadius: "50%", objectFit: "cover" }} />
              <span style={{ color: "rgba(255,255,255,0.62)", fontSize: "13px", fontFamily: ff }}>
                {author.name} · {relativeDate(post.publishedAt)} · {post.readTime}
              </span>
            </div>
          )}
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            color: hov ? "#7fe6ef" : "#57d7e2",
            fontSize: "15px", fontWeight: 700, fontFamily: ff, transition: "color 0.15s",
          }}>
            Read article
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ transition: "transform 0.15s", transform: hov ? "translateX(3px)" : "translateX(0)" }}>
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
      </div>
    </Link>
  );
}

// ── Editor's picks — compact rows beside the hero, spread to fill its height ───
// `curated` decides the heading: calling a sort order "Editor's picks" is
// the kind of thing readers notice, and it surfaced "Best SEO Services in
// Dubai" as a pick.
function EditorsPicks({ posts, curated }: { posts: Post[]; curated: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px", height: "100%" }}>
      <div style={{
        fontSize: "12px",
        fontWeight: 700,
        fontFamily: ff,
        letterSpacing: "0.08em",
        textTransform: "uppercase" as const,
        color: "rgba(255,255,255,0.62)",
      }}>{curated ? "Essential guides" : "Longest reads"}</div>
      {/* Spread the picks so the column always matches the hero image's height */}
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", flex: 1, gap: "16px" }}>
      {posts.map((post) => {
        const cat = getCategoryBySlug(post.categories[0]);
        return (
          <Link
            key={post.slug}
            href={`/blog/${post.slug}`}
            style={{ display: "flex", gap: "14px", alignItems: "flex-start", textDecoration: "none", padding: "4px 0" }}
          >
            <div style={{ width: "64px", height: "64px", flexShrink: 0, borderRadius: "10px", overflow: "hidden", background: "#16201f" }}>
              <CoverImage post={post} imgStyle={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
            <div>
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {cat && (
                  <span style={{
                    fontSize: "10px", fontWeight: 700, fontFamily: ff,
                    letterSpacing: "0.07em", textTransform: "uppercase" as const, color: cat.color,
                  }}>{cat.label}</span>
                )}
                {hasDiagram(post.slug) && (
                  <span style={{
                    fontSize: "10px", fontWeight: 700, fontFamily: ff,
                    letterSpacing: "0.07em", textTransform: "uppercase" as const, color: "#57d7e2",
                  }}>· Interactive</span>
                )}
              </span>
              <div style={{
                color: "#fff", fontSize: "15px", fontWeight: 600, fontFamily: ff,
                lineHeight: 1.3, marginTop: "3px",
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const, overflow: "hidden",
              }}>{stripTitleSuffix(post.title)}</div>
              <div style={{ color: "rgba(255,255,255,0.62)", fontSize: "12px", fontFamily: ff, marginTop: "4px" }}>{post.readTime}</div>
            </div>
          </Link>
        );
      })}
      </div>
    </div>
  );
}

// ── See all link ──────────────────────────────────────────────────────────────
function SeeAllLink({ href }: { href: string }) {
  const [hov, setHov] = useState(false);
  return (
    <Link
      href={href}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        color: hov ? "#7fe6ef" : "#57d7e2",
        fontSize: "14px",
        fontWeight: 700,
        fontFamily: ff,
        textDecoration: "none",
        whiteSpace: "nowrap" as const,
        flexShrink: 0,
      }}
    >
      See all
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ transition: "transform 0.15s", transform: hov ? "translateX(3px)" : "translateX(0)" }}>
        <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </Link>
  );
}

// ── Category preview row — header + "See all" + recent cards ──────────────────
function CategoryRow({ category, posts }: { category: (typeof CATEGORY_LIST)[number]; posts: Post[] }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ marginBottom: "56px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", marginBottom: "18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
          <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: category.color, flexShrink: 0 }} />
          <span style={{ fontSize: "18px", fontWeight: 800, fontFamily: ff, color: "#fff", letterSpacing: "-0.01em" }}>{category.label}</span>
        </div>
        <SeeAllLink href={`/blog/category/${category.slug}`} />
      </div>
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {posts.map((post) => (
            <CompactRow key={post.slug} post={post} />
          ))}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px" }}>
          {posts.map((post) => (
            <BlogCard key={post.slug} post={post} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Grid card ─────────────────────────────────────────────────────────────────
// `matchNote` explains a search hit the title doesn't account for, so a result
// that matched on a tag doesn't look like a mistake.
function BlogCard({ post, matchNote }: { post: Post; matchNote?: string }) {
  const [hov, setHov] = useState(false);
  const summary = postExcerpt(post);
  const cat = getCategoryBySlug(post.categories[0]);
  const accent = cat?.color ?? "#57d7e2";
  return (
    <Link
      href={`/blog/${post.slug}`}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex",
        flexDirection: "column",
        background: "#111",
        border: `1px solid ${hov ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.07)"}`,
        borderRadius: "14px",
        overflow: "hidden",
        textDecoration: "none",
        transition: "border-color 0.2s, transform 0.2s, background 0.2s",
        transform: hov ? "translateY(-2px)" : "translateY(0)",
        cursor: "pointer",
        height: "100%",
      }}
    >
      <div style={{ height: "200px", overflow: "hidden", flexShrink: 0, position: "relative" }}>
        <CoverImage post={post} imgStyle={{
          width: "100%", height: "100%", objectFit: "cover", display: "block",
          transform: hov ? "scale(1.03)" : "scale(1)", transition: "transform 0.3s",
        }} />
      </div>

      <div style={{ padding: "22px 24px 24px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
          <CategoryPill slug={post.categories[0]} />
          {/* 37 posts carry a hand-built interactive explainer and nothing said
              so. It is the one thing competitors do not have. */}
          {hasDiagram(post.slug) && (
            <span style={{
              display: "inline-block", padding: "3px 10px", borderRadius: "4px",
              fontSize: "11px", fontWeight: 700, fontFamily: ff,
              letterSpacing: "0.07em", textTransform: "uppercase" as const,
              color: "#57d7e2", background: "rgba(87,215,226,0.08)",
              border: "1px solid rgba(87,215,226,0.2)", whiteSpace: "nowrap" as const,
            }}>Interactive</span>
          )}
        </div>

        <h3 style={{
          color: "#fff",
          fontSize: "20px",
          fontWeight: 700,
          fontFamily: ff,
          margin: "0 0 10px",
          lineHeight: 1.3,
          letterSpacing: "-0.015em",
          textWrap: "balance" as const,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical" as const,
          overflow: "hidden",
        }}>{stripTitleSuffix(post.title)}</h3>

        {/* The takeaway line does the work the author row used to occupy.
            Three adjacent cards can read "Best X in 2026", "Best Y in 2026",
            "Best Z in 2026" — this is what tells them apart. */}
        {summary && (
          <p style={{
            color: "rgba(255,255,255,0.6)",
            fontSize: "14px",
            fontFamily: ff,
            lineHeight: 1.55,
            margin: "0 0 12px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical" as const,
            overflow: "hidden",
          }}>{summary}</p>
        )}

        {matchNote && (
          <div style={{ color: "#57d7e2", fontSize: "12px", fontFamily: ff, fontWeight: 600, marginBottom: "10px" }}>
            {matchNote}
          </div>
        )}

        {/* One author, ~8 minutes and this week's date on all 421 posts: the
            avatar row said nothing and cost 40px a card. What is left is the
            part that varies. */}
        <div style={{ marginTop: "auto", paddingTop: "14px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <span style={{ color: "rgba(255,255,255,0.62)", fontSize: "13px", fontFamily: ff }}>
            {[relativeDate(post.publishedAt), longReadLabel(post.readingTime)].filter(Boolean).join(" · ")}
          </span>
        </div>
      </div>
    </Link>
  );
}

// ── Category dropdown ─────────────────────────────────────────────────────────
function DropdownItem({ label, count, color, dot, href, current, onSelect }: {
  label: string;
  count: number;
  color: string;
  dot: boolean;
  href: string;
  current: boolean;
  onSelect: () => void;
}) {
  const [hov, setHov] = useState(false);
  return (
    <Link
      href={href}
      onClick={onSelect}
      role="option"
      aria-selected={current}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        width: "100%",
        padding: "9px 12px",
        background: current ? `${color}1f` : hov ? "rgba(255,255,255,0.05)" : "transparent",
        borderRadius: "8px",
        cursor: "pointer",
        fontFamily: ff,
        textDecoration: "none",
        textAlign: "left" as const,
      }}
    >
      <span style={{ width: "9px", height: "9px", borderRadius: dot ? "50%" : "2px", background: color, flexShrink: 0 }} />
      <span style={{ flex: 1, color: current ? "#fff" : "rgba(255,255,255,0.82)", fontSize: "14px", fontWeight: current ? 700 : 500 }}>{label}</span>
      <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", fontWeight: 500 }}>{count}</span>
    </Link>
  );
}

// Navigates to a dedicated category page on selection (no in-page filtering).
function CategoryDropdown({ counts, total, fullWidth }: {
  counts: Record<string, number>;
  total: number;
  fullWidth?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [hov, setHov] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div ref={ref} style={{ position: "relative", width: fullWidth ? "100%" : "auto", flexShrink: 0 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          width: fullWidth ? "100%" : "auto",
          justifyContent: fullWidth ? "space-between" : "center",
          padding: "10px 16px",
          background: (hov || open) ? "#1a1d1d" : "#141717",
          border: `1px solid ${open ? "rgba(87,215,226,0.5)" : "rgba(255,255,255,0.14)"}`,
          borderRadius: "8px",
          color: "#fff",
          fontFamily: ff,
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap" as const,
          transition: "background 0.15s, border-color 0.15s",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "9px" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
            <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="#57d7e2" strokeWidth="2" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="#57d7e2" strokeWidth="2" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="#57d7e2" strokeWidth="2" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="#57d7e2" strokeWidth="2" />
          </svg>
          Explore categories
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ transition: "transform 0.15s", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}>
          <path d="M6 9l6 6 6-6" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div role="listbox" style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          right: 0,
          left: fullWidth ? 0 : "auto",
          width: fullWidth ? "100%" : "280px",
          background: "#16181a",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "12px",
          padding: "6px",
          zIndex: 30,
          boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
          maxHeight: "min(70vh, 440px)",
          overflowY: "auto",
        }}>
          <DropdownItem label="All articles" count={total} color="#57d7e2" dot={false} href="/blog" current onSelect={close} />
          {CATEGORY_LIST.map((cat) => (
            <DropdownItem
              key={cat.slug}
              label={cat.label}
              count={counts[cat.slug] ?? 0}
              color={cat.color}
              dot
              href={`/blog/category/${cat.slug}`}
              current={false}
              onSelect={close}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Slim search field ─────────────────────────────────────────────────────────
function SearchField({ value, onChange, inputRef, fullWidth }: {
  value: string;
  onChange: (v: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  fullWidth?: boolean;
}) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      background: "#141717",
      border: `1px solid ${focus ? "rgba(87,215,226,0.5)" : "rgba(255,255,255,0.12)"}`,
      borderRadius: "8px",
      width: fullWidth ? "100%" : "260px",
      flexShrink: 0,
      transition: "border-color 0.15s",
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="11" cy="11" r="7" stroke={focus ? "#57d7e2" : "rgba(255,255,255,0.45)"} strokeWidth="2" />
        <path d="M21 21L16.65 16.65" stroke={focus ? "#57d7e2" : "rgba(255,255,255,0.45)"} strokeWidth="2" strokeLinecap="round" />
      </svg>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        placeholder="Search articles"
        aria-label="Search articles"
        style={{
          flex: 1,
          minWidth: 0,
          background: "transparent",
          border: "none",
          outline: "none",
          color: "#fff",
          fontSize: "14px",
          fontFamily: ff,
        }}
      />
      {value ? (
        <button
          onClick={() => onChange("")}
          aria-label="Clear search"
          style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.4)", fontSize: "13px", fontFamily: ff, padding: 0, flexShrink: 0 }}
        >
          Clear
        </button>
      ) : (
        <span className="kbd-hint" aria-hidden="true" style={{ fontSize: "11px", color: "rgba(255,255,255,0.45)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: "4px", padding: "1px 6px", fontFamily: ff, whiteSpace: "nowrap" as const, flexShrink: 0 }}>⌘K</span>
      )}
    </div>
  );
}

// ── Toolbar — search + "Explore categories" dropdown ─────────────────────────
function BlogToolbar({ query, onQuery, counts, inputRef }: {
  query: string;
  onQuery: (v: string) => void;
  counts: Record<string, number>;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const isMobile = useIsMobile();

  return (
    <div style={{ marginTop: isMobile ? "24px" : "48px", marginBottom: "48px" }}>
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <SearchField value={query} onChange={onQuery} inputRef={inputRef} fullWidth />
          <CategoryDropdown counts={counts} total={POSTS.length} fullWidth />
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SearchField value={query} onChange={onQuery} inputRef={inputRef} fullWidth />
          </div>
          <CategoryDropdown counts={counts} total={POSTS.length} />
        </div>
      )}
    </div>
  );
}

// ── Topic strip ───────────────────────────────────────────────────────────────
// Nine topics is this blog's information architecture. Hiding it behind an
// "Explore categories" dropdown hid the map. The strip puts it on the page,
// with counts, plus the diagrams collection — which had no entry point at all.
function TopicStrip({ counts }: { counts: Record<string, number> }) {
  return (
    <nav aria-label="Topics" className="topic-strip">
      <Link href="/blog/diagrams" className="topic-chip topic-chip-diagrams">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="12" width="4" height="9" rx="1" fill="currentColor" opacity="0.55" />
          <rect x="10" y="7" width="4" height="14" rx="1" fill="currentColor" opacity="0.8" />
          <rect x="17" y="3" width="4" height="18" rx="1" fill="currentColor" />
        </svg>
        Diagrams
        <span className="topic-count">{DIAGRAM_SLUGS.length}</span>
      </Link>
      {CATEGORY_LIST.map((cat) => (
        <Link key={cat.slug} href={`/blog/category/${cat.slug}`} className="topic-chip">
          <span className="topic-dot" style={{ background: cat.color }} />
          {cat.label}
          <span className="topic-count">{counts[cat.slug] ?? 0}</span>
        </Link>
      ))}
    </nav>
  );
}

// ── Compact row — the mobile list item ────────────────────────────────────────
// 35 cards with a 200px cover each is a 15,000px scroll on a phone. Same
// information, a third of the height.
function CompactRow({ post }: { post: Post }) {
  const cat = getCategoryBySlug(post.categories[0]);
  return (
    <Link href={`/blog/${post.slug}`} className="compact-row">
      <div className="compact-row-cover">
        <CoverImage post={post} imgStyle={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <span style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "3px" }}>
          {cat && (
            <span style={{
              fontSize: "10px", fontWeight: 800, fontFamily: ff,
              letterSpacing: "0.08em", textTransform: "uppercase" as const, color: cat.color,
            }}>{cat.label}</span>
          )}
          {hasDiagram(post.slug) && (
            <span style={{
              fontSize: "10px", fontWeight: 800, fontFamily: ff,
              letterSpacing: "0.08em", textTransform: "uppercase" as const, color: "#57d7e2",
            }}>· Interactive</span>
          )}
        </span>
        <div style={{
          color: "#fff", fontSize: "15px", fontWeight: 600, fontFamily: ff,
          lineHeight: 1.35, letterSpacing: "-0.01em",
          display: "-webkit-box", WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical" as const, overflow: "hidden",
        }}>{stripTitleSuffix(post.title)}</div>
        <div style={{ color: "rgba(255,255,255,0.62)", fontSize: "12px", fontFamily: ff, marginTop: "4px" }}>
          {[relativeDate(post.publishedAt), longReadLabel(post.readingTime)].filter(Boolean).join(" · ")}
        </div>
      </div>
    </Link>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function BlogIndex() {
  useSeoMeta({
    title: "SEO Blog | Strategy & Insights for Growth Companies",
    description: "Practical SEO insights, strategy guides, and expert commentary for marketing leaders and founders serious about growing organic search performance.",
    canonical: "/blog",
  });
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const searchRef = useRef<HTMLInputElement>(null);

  // ⌘K / Ctrl-K focuses search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Reset pagination whenever the result set changes
  useEffect(() => { setVisible(PAGE_SIZE); }, [query]);

  const { sorted, hero, picks, picksAreCurated } = useMemo(() => deriveSections(POSTS), []);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const p of POSTS) for (const cat of p.categories) c[cat] = (c[cat] ?? 0) + 1;
    return c;
  }, []);

  // The biggest categories, each with its 3 most recent articles, for the
  // preview modules. A post carrying two categories is claimed by the first
  // row that takes it — otherwise it renders twice on one screen.
  const categoryRows = useMemo(() => {
    // Seeded with the hero and the editor's picks: those already have a slot
    // higher up the page, and a post can hold several categories.
    const claimed = new Set<string>([...(hero ? [hero.slug] : []), ...picks.map((p) => p.slug)]);
    const candidates = [...CATEGORY_LIST]
      .map((category) => ({ category, posts: sorted.filter((p) => p.categories.includes(category.slug)) }))
      .filter((x) => x.posts.length >= 3)
      .sort((a, b) => b.posts.length - a.posts.length)
      .slice(0, 4);

    const rows: { category: (typeof CATEGORY_LIST)[number]; posts: Post[] }[] = [];
    for (const { category, posts } of candidates) {
      const picked = posts.filter((p) => !claimed.has(p.slug)).slice(0, 3);
      if (picked.length < 3) continue;
      for (const p of picked) claimed.add(p.slug);
      rows.push({ category, posts: picked });
    }
    return rows;
  }, [sorted, hero, picks]);

  const q = query.trim();
  const isDefaultView = !q;

  // Result set for the main grid. Categories navigate to their own pages, so the
  // only in-page filter here is search.
  const results = useMemo(() => {
    if (q) {
      // Ranked by where the query matched, not by date — a tag-only match on
      // the newest post used to outrank every article with the phrase in its
      // title.
      return searchPosts(sorted, q);
    }
    // Don't repeat anything already shown above: the hero, the editor's picks,
    // or the three cards in each category row. Without the category rows in
    // this set the same article appeared twice within one screen.
    const shownAbove = new Set<string>([
      ...(hero ? [hero.slug] : []),
      ...picks.map((p) => p.slug),
      ...categoryRows.flatMap((row) => row.posts.map((p) => p.slug)),
    ]);
    return sorted.filter((p) => !shownAbove.has(p.slug));
  }, [sorted, q, hero, picks, categoryRows]);

  const shown = results.slice(0, visible);
  const hasMore = results.length > visible;

  return (
    <SiteLayout>
      <div style={{
        background: "#0a0c0c",
        minHeight: "100vh",
        paddingTop: "96px",
        paddingBottom: "100px",
      }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px" }}>

          {/* SEO heading — marketing hero removed per request; kept visually hidden so the listing still has an h1 */}
          <h1 style={{ position: "absolute", width: "1px", height: "1px", padding: 0, margin: "-1px", overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>
            Indexed SEO blog — strategy, AI search &amp; organic growth insights
          </h1>

          {/* Search + "Explore categories" dropdown */}
          <BlogToolbar query={query} onQuery={setQuery} counts={counts} inputRef={searchRef} />

          {/* The map, on the page rather than behind a dropdown. */}
          {isDefaultView && <TopicStrip counts={counts} />}

          {/* ── Default browse view ── */}
          {isDefaultView && hero && (
            <>
              {/* Hero module */}
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1.6fr 1fr",
                gap: "24px",
                marginBottom: "56px",
                alignItems: "stretch",
              }}>
                <FeaturedPost post={hero} />
                {picks.length > 0 && <EditorsPicks posts={picks} curated={picksAreCurated} />}
              </div>

              {/* Category preview modules. One promo module, not two: the
                  index had eight conversion links on a content hub, and the
                  AI-visibility block was the loudest element on the page. It
                  now sits once, after the reader is past the flagship rows. */}
              {categoryRows.map((row, i) => (
                <Fragment key={row.category.slug}>
                  {i === 3 && (
                    <div style={{ marginBottom: "56px" }}>
                      <LLMVisibilityPromoBlock />
                    </div>
                  )}
                  <CategoryRow category={row.category} posts={row.posts} />
                </Fragment>
              ))}

              {/* All articles */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", marginBottom: "18px" }}>
                <div style={{ fontSize: "18px", fontWeight: 800, fontFamily: ff, color: "#fff", letterSpacing: "-0.01em" }}>
                  All articles
                </div>
                <SeeAllLink href="/blog/category/latest" />
              </div>
            </>
          )}

          {/* ── Filtered / search view: results count ── */}
          {!isDefaultView && (
            <div style={{ color: "rgba(255,255,255,0.68)", fontSize: "15px", fontFamily: ff, marginBottom: "24px" }}>
              {results.length === 0
                ? "No articles found"
                : `${results.length} article${results.length === 1 ? "" : "s"}${q ? ` for "${query.trim()}"` : ""}`}
            </div>
          )}

          {/* Empty state */}
          {results.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0 80px", color: "rgba(255,255,255,0.6)", fontFamily: ff, fontSize: "16px" }}>
              No articles match your search — try different keywords.
            </div>
          )}

          {/* All-articles feed. A phone gets compact rows: 35 cards with a
              200px cover each is a 15,000px scroll. */}
          {shown.length > 0 && (isMobile ? (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {shown.map((post) => (
                <CompactRow key={post.slug} post={post} />
              ))}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px" }}>
              {shown.map((post) => (
                <BlogCard key={post.slug} post={post} matchNote={q ? matchReason(post, q) : ""} />
              ))}
            </div>
          ))}

          {/* Load more */}
          {hasMore && (
            <div style={{ textAlign: "center", marginTop: "48px" }}>
              <button
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                style={{
                  display: "inline-block",
                  background: "transparent",
                  border: "1px solid rgba(87,215,226,0.35)",
                  color: "#57d7e2",
                  fontSize: "15px",
                  fontWeight: 700,
                  fontFamily: ff,
                  borderRadius: "10px",
                  padding: "13px 32px",
                  cursor: "pointer",
                }}
              >
                Load more articles
              </button>
              <div style={{ color: "rgba(255,255,255,0.62)", fontSize: "13px", fontFamily: ff, marginTop: "12px" }}>
                Showing {shown.length} of {results.length}
              </div>
            </div>
          )}

          {/* Someone who has scrolled the whole index is browsing rather than
              reading one thing — a better moment to ask for an email than the
              bottom of a single article they may have landed on from search. */}
          <NewsletterForm source="blog-index" idPrefix="blog-index-subscribe" theme="dark" />

        </div>
      </div>
    </SiteLayout>
  );
}
